import { Grid, Typography } from "@material-ui/core";
import TextField from "@material-ui/core/TextField";
import { makeStyles } from "@material-ui/core/styles";
import * as React from "react";
import { FC, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";
import { Switzerland } from "../../../../data/constants";
import Places from "../../../../data/placeOfOrigin.json";
import { GetTranslation } from "../../../../translations/GetTranslation";
import { TranslationKey, isTranslationKey } from "../../../../translations/TranslationKey";
import FormGridCell from "../../../common/FormGridCell";
import { useSignUpContext } from "../../CertificationDataWizardContext/CertificationDataWizardContext";
import {
  CompanySelectField,
  CountrySelectField,
  DateInput,
  GenderSelectField,
  PlaceOfOriginSelectField,
  TextInput,
} from "../shared/Fields";
import { getValidDate } from "../../../../utils/Dates";

const useStyles = makeStyles({
  businessData: {
    paddingTop: 25,
  },
});

const CandidateFields: FC = () => {
  const intl = useIntl();
  const {
    data: { candidate },
  } = useSignUpContext();
  const { watch, setValue } = useFormContext();

  const homeCountry = watch("homeCountry");
  const placeOfOrigin = watch("placeOfOrigin");
  const canton = watch("canton");

  const classes = useStyles();

  useEffect(() => {
    setValue("nationalityIsSwiss", homeCountry === Switzerland);
  }, [homeCountry, setValue]);

  useEffect(() => {
    const cantonObj = Places.find((place) => place.value === placeOfOrigin);
    setValue("canton", cantonObj?.canton || "");
  }, [placeOfOrigin, setValue]);

  const getCantonDisplayValue = () => {
    if (!canton) {
      return "";
    }
    const key = `canton.${canton.toLowerCase()}`;
    if (isTranslationKey(key)) {
      return GetTranslation(intl, key as TranslationKey);
    }
    return canton;
  };

  return (
    <>
      <Typography variant="h6">{GetTranslation(intl, "person.section.personalDetails")}</Typography>
      <Grid container={true} spacing={4}>
        <FormGridCell size="third">
          <TextInput
            disabled={candidate?.disablePersonalFields}
            name="firstName"
            label={GetTranslation(intl, "person.field.firstName")}
            required
          />
        </FormGridCell>
        <FormGridCell size="third">
          <TextInput
            disabled={candidate?.disablePersonalFields}
            name="lastName"
            label={GetTranslation(intl, "person.field.lastName")}
            required
          />
        </FormGridCell>
        <FormGridCell size="third">
          <GenderSelectField name="gender" defaultValue={candidate?.gender} required />
        </FormGridCell>
        <Grid item={true} xs={12} sm={6} md={4} lg={4} xl={4} style={{ paddingTop: 0, paddingBottom: 0 }}>
          <DateInput
            disabled={candidate?.disablePersonalFields}
            name="birthdate"
            defaultValue={getValidDate(candidate.birthdate)}
            label={GetTranslation(intl, "person.field.birthDate")}
          />
        </Grid>
        <FormGridCell size="third">
          <TextInput
            disabled={candidate?.disablePersonalFields}
            name="ahvNumber"
            label={GetTranslation(intl, "person.field.ahvNumber")}
            required
          />
        </FormGridCell>
        <FormGridCell size="third">
          <TextInput name="emailAddress" label={GetTranslation(intl, "person.field.email")} required />
        </FormGridCell>
        <FormGridCell size="third">
          <TextInput name="phoneNumber" label={GetTranslation(intl, "person.field.phoneNumber")} />
        </FormGridCell>
        <FormGridCell size="third">
          <TextInput name="mobileNumber" label={GetTranslation(intl, "person.field.mobileNumber")} />
        </FormGridCell>
        <FormGridCell size="third">
          <TextInput name="ciceroNumber" label={GetTranslation(intl, "person.field.ciceroNumber")} />
        </FormGridCell>
        <FormGridCell size="third">
          <CountrySelectField
            disabled={candidate?.disablePersonalFields}
            name="homeCountry"
            label={GetTranslation(intl, "person.field.homeCountry")}
            defaultValue={candidate.homeCountry}
            required
          />
        </FormGridCell>
        {homeCountry === Switzerland && (
          <>
            <FormGridCell size="third">
              <PlaceOfOriginSelectField
                disabled={candidate?.disablePersonalFields}
                name="placeOfOrigin"
                defaultValue={candidate.placeOfOrigin}
                required
              />
            </FormGridCell>
            <FormGridCell size="third">
              <TextField
                required
                fullWidth={true}
                inputProps={{ readOnly: true }}
                label={GetTranslation(intl, "candidate.field.canton")}
                value={getCantonDisplayValue()}
              />
            </FormGridCell>
          </>
        )}
      </Grid>
      <Typography variant="h6" className={classes.businessData}>
        {GetTranslation(intl, "person.section.businessData")}
      </Typography>
      <Grid container={true} spacing={4}>
        <FormGridCell size="third">
          <CompanySelectField
            name="employerInformation"
            label={GetTranslation(intl, "person.field.employer")}
            defaultValue={candidate.employerInformation}
            required
          />
        </FormGridCell>
      </Grid>
    </>
  );
};

export default CandidateFields;
